/**
 * YouTubePlayer
 * サムネ枠に YouTube iframe を差し込んでインライン再生する共通処理。
 *
 * - IntersectionObserver でルートを監視し、画面内へ入ったら再生・画面外へ出たら一時停止。
 *   （一時停止時も iframe は残したまま postMessage で pause/play＝再生位置を保持。YouTube IFrame API = enablejsapi）
 * - ブラウザ規約により autoplay は mute 必須。
 * - 動画IDは対象要素の data-youtube-id、差し込み先は holderSelector で指定する。
 *
 * 単体設置＝.p-movie（app.js から直接インスタンス化）／タブ切替つき＝SeminarVideo が継承。
 */
export default class YouTubePlayer {
  /**
   * @param {HTMLElement} root 監視するルート要素
   * @param {{items?: HTMLElement[], activeIndex?: number, holderSelector?: string, iframeClass?: string, threshold?: number}} options
   */
  constructor(root, options = {}) {
    this.root = root || null;
    this.items = options.items || (root ? [root] : []);
    this.activeIndex = options.activeIndex || 0;
    this.holderSelector = options.holderSelector || '';
    this.iframeClass = options.iframeClass || '';
    if (!this.root || this.items.length === 0) {
      return;
    }
    this.observe(typeof options.threshold === 'number' ? options.threshold : 0.25);
  }

  /**
   * @param {number} threshold
   */
  observe(threshold) {
    if (!('IntersectionObserver' in window)) {
      this.mount(this.activeItem());
      return;
    }
    // 画面内→（無ければ差し込み）再生／画面外→一時停止。初期表示もこれで発火
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.resumeOrMount();
        } else {
          this.pauseActive();
        }
      });
    }, { threshold });
    this.observer.observe(this.root);
  }

  activeItem() {
    return this.items[this.activeIndex] || this.items[0];
  }

  /**
   * @param {HTMLElement} item
   */
  holderOf(item) {
    return item ? item.querySelector(this.holderSelector) : null;
  }

  /**
   * @param {HTMLElement} item
   */
  iframeOf(item) {
    const holder = this.holderOf(item);
    return holder ? holder.querySelector('iframe') : null;
  }

  resumeOrMount() {
    const item = this.activeItem();
    const iframe = this.iframeOf(item);
    if (iframe) {
      this.command(iframe, 'playVideo');
    } else {
      this.mount(item);
    }
  }

  pauseActive() {
    const iframe = this.iframeOf(this.activeItem());
    if (iframe) {
      this.command(iframe, 'pauseVideo');
    }
  }

  /**
   * @param {HTMLElement} item
   */
  mount(item) {
    const holder = this.holderOf(item);
    const videoId = item && item.dataset.youtubeId;
    if (!holder || !videoId || holder.querySelector('iframe')) {
      return;
    }
    const iframe = document.createElement('iframe');
    iframe.className = this.iframeClass;
    iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&playsinline=1&enablejsapi=1`;
    iframe.title = '動画プレイヤー';
    iframe.loading = 'lazy';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    holder.appendChild(iframe);
    holder.classList.add('is-playing');
  }

  /**
   * @param {HTMLElement} item
   */
  unmount(item) {
    const holder = this.holderOf(item);
    if (!holder) {
      return;
    }
    const iframe = holder.querySelector('iframe');
    if (iframe) {
      iframe.remove(); // src除去＝再生停止
    }
    holder.classList.remove('is-playing');
  }

  /**
   * YouTube IFrame API へ postMessage でコマンド送信（enablejsapi=1 前提）
   * @param {HTMLIFrameElement} iframe
   * @param {string} func 'playVideo' | 'pauseVideo'
   */
  command(iframe, func) {
    if (!iframe || !iframe.contentWindow) {
      return;
    }
    try {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func, args: '' }),
        '*'
      );
    } catch (e) {
      // クロスオリジン等で失敗しても致命的でないため無視
    }
  }
}
